import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  FileText, 
  Loader, 
  AlertCircle, 
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Sparkles,
  Download,
  Shield,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { getFilingTypeTag, formatDateForDisplay } from '../../utils/docket-search';
import { supabase, STORAGE_BUCKET } from '../../lib/supabase';
import { generateIndividualSummary } from '../../lib/individualSummaryService';
import { batchCheckSpamFilers } from '../../lib/spamFilerService';
import { fetchProceedingsFilings } from '../../lib/proceedingsFilingRetrieval';
import {
  uploadFilingsParallelWithResults,
  type MinimalFiling,
} from '../../lib/parallelFilingUpload';
import { recordDocumentUpload } from '../../lib/documentTrackingService';
import { IndividualSummaryDigestPanel } from './IndividualSummaryDigestPanel';
import {
  getProceedingFilingPeriod,
  isCommentOrReplyFiling,
} from '../../utils/proceedingsFilingsUi';

const BULK_DOWNLOAD_CONCURRENCY = 5;
/** Max in-flight individual-summary requests when batching from the Filings tab. */
const INDIVIDUAL_SUMMARY_BATCH_CONCURRENCY = 5;
const STORAGE_LIST_PAGE = 1000;
const UPLOADED_DOCS_CHUNK = 150;

function isStorageObjectKey(fileName: string, submissionId: string): boolean {
  const parts = fileName.split('___');
  return parts.length >= 3 && parts[0] === submissionId;
}

/** Paginated storage list (files and folder placeholders). */
async function listAllStorageEntries(pathPrefix: string) {
  const all: { name: string; id: string | null }[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .list(pathPrefix, { limit: STORAGE_LIST_PAGE, offset });
    if (error) throw error;
    const batch = data ?? [];
    all.push(
      ...batch.map((e: { name: string; id: string | null }) => ({ name: e.name, id: e.id }))
    );
    if (batch.length < STORAGE_LIST_PAGE) break;
    offset += STORAGE_LIST_PAGE;
  }
  return all;
}

/** Maps submission_id -> storage object under ecfs/{docket}/ (first win per submission). */
async function buildSubmissionIdIndex(docketNumber: string) {
  const map = new Map<string, { filename: string; filingType: string }>();
  const rootEntries = await listAllStorageEntries(`ecfs/${docketNumber}`);
  const filingTypeFolders = rootEntries.filter((e) => e.id === null);

  await Promise.all(
    filingTypeFolders.map(async (folder) => {
      const prefix = `ecfs/${docketNumber}/${folder.name}`;
      const entries = await listAllStorageEntries(prefix);
      for (const file of entries) {
        const parts = file.name.split('___');
        if (parts.length >= 3 && !map.has(parts[0])) {
          map.set(parts[0], { filename: file.name, filingType: folder.name });
        }
      }
    })
  );
  return map;
}

type UploadedDocRow = { submission_id: string; file_name: string; filing_type: string | null };

async function fetchUploadedDocumentsChunked(
  submissionIds: string[],
  docketNumber: string
): Promise<Map<string, UploadedDocRow>> {
  const bySubmission = new Map<string, UploadedDocRow>();
  for (let i = 0; i < submissionIds.length; i += UPLOADED_DOCS_CHUNK) {
    const chunk = submissionIds.slice(i, i + UPLOADED_DOCS_CHUNK);
    const { data, error } = await supabase
      .from('uploaded_documents')
      .select('submission_id, file_name, filing_type')
      .eq('docket_number', docketNumber)
      .in('submission_id', chunk);
    if (error) {
      console.warn('fetchUploadedDocumentsChunked:', error);
      continue;
    }
    for (const row of data ?? []) {
      if (!bySubmission.has(row.submission_id)) {
        bySubmission.set(row.submission_id, row as UploadedDocRow);
      }
    }
  }
  return bySubmission;
}

function resolveFileForBulkZip(
  submissionId: string,
  dbRow: UploadedDocRow | undefined,
  index: Map<string, { filename: string; filingType: string }>
): { filename: string; filingType: string } | null {
  if (dbRow?.file_name && isStorageObjectKey(dbRow.file_name, submissionId)) {
    return {
      filename: dbRow.file_name,
      filingType: dbRow.filing_type || 'UNKNOWN',
    };
  }
  const fromIndex = index.get(submissionId);
  if (fromIndex) {
    return {
      filename: fromIndex.filename,
      filingType: fromIndex.filingType || 'UNKNOWN',
    };
  }
  return null;
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
  onProgress?: (completed: number, total: number) => void
): Promise<void> {
  const total = items.length;
  if (total === 0) return;
  let next = 0;
  let completed = 0;
  const safeConc = Math.min(concurrency, total);

  async function runWorker() {
    for (;;) {
      const i = next++;
      if (i >= total) return;
      try {
        await worker(items[i], i);
      } finally {
        completed++;
        onProgress?.(completed, total);
      }
    }
  }

  await Promise.all(Array.from({ length: safeConc }, () => runWorker()));
}

type DownloadZipProgressState =
  | null
  | { phase: 'index'; current: number; total: number }
  | { phase: 'download'; current: number; total: number }
  | { phase: 'zip'; current: number; total: number };

interface Filing {
  id_submission: string;
  filers?: Array<{ name: string }>;
  date_received?: string;
  date_submission?: string;
  submissiontype?: { short: string };
  filing_type?: string;
  proceedings?: Array<{ name: string }>;
}

interface FilingsSectionProps {
  docketNumber: string;
  onProcessForSummary: (filingIds: string[]) => void;
  /** When PN prevails, bounds comment/reply bucketing + ECFS date window (same shape as DB nprm_timelines row). */
  syntheticTimelineForFilings?: NPRMTimeline | null;
  /** Authoritative substantive filing count (ECFS aggregation). Drives the
   *  honest "Showing X of N" label when the retrieved list is a subset. */
  authoritativeCount?: number | null;
}

export interface NPRMTimeline {
  id: string;
  docket_number: string;
  comments_period_start: string;
  comments_period_end: string | null;
  reply_comments_period_start: string | null;
  reply_comments_period_end: string | null;
}

interface PeriodSubmission {
  submission_id: string;
  period_type: 'comments' | 'reply_comments';
  date_submission?: string | null;
  nprm_timeline_id?: string;
}

interface SpamFilteredFilingDebug {
  submissionId: string;
  filerName: string;
  reason?: string;
}

/**
 * Per-filing retrievability status used to decide whether a row's document is
 * available for summarization.
 *
 * - `uploaded`: a storage object + (usually) an `uploaded_documents` row exist
 *   for this submission under the current docket.
 * - `unavailable`: repair pass attempted but upload-ecfs-files rejected the
 *   document for a reason that will not change on retry (size > 50 MB,
 *   unsupported extension, spam filer, etc.). Summarize / View disabled.
 */
type RetrievabilityStatus = 'uploaded' | 'unavailable';
interface Retrievability {
  status: RetrievabilityStatus;
  reason?: string;
}

type RepairProgressState =
  | null
  | { phase: 'scan' }
  | { phase: 'upload'; current: number; total: number };

const MAX_REPAIR_UPLOADS_PER_RUN = 50;

function humanizeRetrievabilityReason(reason?: string): string {
  if (!reason) return 'Document is not available in Supabase storage.';
  const lower = reason.toLowerCase();
  if (lower.includes('exceed') || lower.includes('size')) {
    return 'File exceeds the 50 MB upload limit.';
  }
  if (lower.includes('file type') || lower.includes('extension') || lower.includes('not allowed')) {
    return 'File type is not supported for summary retrieval (allowed: PDF, DOC/DOCX, TXT, HTML).';
  }
  if (lower.includes('spam')) {
    return 'Filing was flagged as spam and is excluded from summary retrieval.';
  }
  if (lower.includes('express')) {
    return 'Express comments are excluded from summary retrieval.';
  }
  return reason;
}

export const FilingsSection: React.FC<FilingsSectionProps> = ({
  docketNumber,
  onProcessForSummary: _onProcessForSummary,
  syntheticTimelineForFilings = null,
  authoritativeCount = null,
}) => {
  const [allFilings, setAllFilings] = useState<Filing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFiling, setExpandedFiling] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [nprmTimelines, setNprmTimelines] = useState<NPRMTimeline[]>([]);
  const [periodSubmissions, setPeriodSubmissions] = useState<PeriodSubmission[]>([]);
  const [periodSubmissionsMap, setPeriodSubmissionsMap] = useState<Map<string, PeriodSubmission>>(new Map());
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [timelineResolvedForDocket, setTimelineResolvedForDocket] = useState<string | null>(null);
  const [activePeriodTab, setActivePeriodTab] = useState<'comments' | 'reply_comments' | 'other'>('comments');
  const [documentLimit, setDocumentLimit] = useState<number>(200);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  // Raw number of rows the retrieval returned (pre express/spam filtering). We
  // only offer "load more" when this hit the cap — otherwise the gap between the
  // filtered list and the ECFS total is just express/spam rows, not truncation.
  const [retrievedCount, setRetrievedCount] = useState<number>(0);
  const [hideSpamFilings, setHideSpamFilings] = useState<boolean>(true);
  const [exPartesOnly, setExPartesOnly] = useState<boolean>(false);
  const [exParteAdvocacy, setExParteAdvocacy] = useState<
    Array<{ submission_id: string; filer: string; date: string; position: string }>
  >([]);
  const [loadingAdvocacy, setLoadingAdvocacy] = useState(false);
  const [advocacyError, setAdvocacyError] = useState(false);

  // Deep-link from the digest "view all ex partes" overflow link:
  // /proceedings/{docket}?view=exparte opens the ex-parte view directly.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('view') === 'exparte') {
      setExPartesOnly(true);
    }
  }, []);
  // Resync state for the Reload button. When non-null the button shows a
  // spinner + status caption, and the click handler refuses to start a
  // second resync until the first finishes. resyncStatus is rendered next
  // to the spinner so the user knows we are actually pulling fresh ECFS
  // data (the previous Reload button just re-queried the local DB —
  // misleading icon, no real refresh).
  const [resyncing, setResyncing] = useState<boolean>(false);
  const [resyncStatus, setResyncStatus] = useState<string | null>(null);
  const [spamFilteredCount, setSpamFilteredCount] = useState<number>(0);
  const [spamFilteredDebug, setSpamFilteredDebug] = useState<SpamFilteredFilingDebug[]>([]);
  const [spamBySubmissionId, setSpamBySubmissionId] = useState<Map<string, { filerName: string; reason?: string }>>(new Map());
  
  // State for individual summary generation
  const [generatingSummaries, setGeneratingSummaries] = useState<Set<string>>(new Set());
  const [generatedSummaries, setGeneratedSummaries] = useState<Map<string, string>>(new Map());
  const [summaryErrors, setSummaryErrors] = useState<Map<string, string>>(new Map());
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());
  
  // State for batch generation
  const [isGeneratingAll, setIsGeneratingAll] = useState<boolean>(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; currentFiling: string | null } | null>(null);
  
  // Bulk ZIP: null = idle; otherwise phase + counts for toolbar feedback
  const [downloadZipProgress, setDownloadZipProgress] = useState<DownloadZipProgressState>(null);
  const [openingDocumentIds, setOpeningDocumentIds] = useState<Set<string>>(new Set());

  // Per-submission retrievability for Comment / Reply Comment rows. Populated
  // by the repair pass (see useEffect below). Only `unavailable` entries are
  // UI-relevant; a missing key simply means "not checked / assumed uploaded".
  const [retrievability, setRetrievability] = useState<Map<string, Retrievability>>(new Map());
  const [repairProgress, setRepairProgress] = useState<RepairProgressState>(null);
  // Gate the repair pass so it runs once per (docket + filings-length) pair.
  const repairRanRef = useRef<string | null>(null);

  const activeTimeline = useMemo(
    () => syntheticTimelineForFilings ?? nprmTimelines[0] ?? null,
    [syntheticTimelineForFilings, nprmTimelines]
  );

  const fetchTimelineData = async () => {
    if (!docketNumber) return;

    try {
      setLoadingTimeline(true);
      setTimelineResolvedForDocket(null);
      
      // Fetch NPRM timelines
      const { data: timelines, error: timelineError } = await supabase
        .from('nprm_timelines')
        .select('*')
        .eq('docket_number', docketNumber)
        .order('fcc_release_date', { ascending: false });

      if (timelineError) {
        console.error('Error fetching timelines:', timelineError);
      } else {
        setNprmTimelines(timelines || []);
      }

      // Fetch period_submissions - this is the primary source for period categorization
      const { data: periodData, error: periodError } = await supabase
        .from('period_submissions')
        .select('submission_id, period_type, date_submission, nprm_timeline_id')
        .eq('docket_number', docketNumber);

      if (periodError) {
        // Log error but continue - we'll use date calculations as fallback
        console.warn('Error fetching period_submissions:', periodError.message);
        setPeriodSubmissions([]);
        setPeriodSubmissionsMap(new Map());
      } else {
        const periodList = periodData || [];
        setPeriodSubmissions(periodList);
        
        // Create a Map for O(1) lookups by submission_id
        const periodMap = new Map<string, PeriodSubmission>();
        periodList.forEach(ps => {
          periodMap.set(ps.submission_id, ps);
        });
        setPeriodSubmissionsMap(periodMap);
        
        if (periodList.length > 0) {
          console.log(`Loaded ${periodList.length} period_submissions entries for docket ${docketNumber}`);
        }
      }
    } catch (err) {
      console.error('Error fetching timeline data:', err);
    } finally {
      setLoadingTimeline(false);
      setTimelineResolvedForDocket(docketNumber);
    }
  };

  const fetchFilings = useCallback(async (timeline: NPRMTimeline | null = null) => {
    if (!docketNumber) return;

    try {
      setLoading(true);
      setError(null);

      const retrieval = await fetchProceedingsFilings(docketNumber, timeline || undefined, documentLimit);
      setRetrievedCount(retrieval.filings.length);
      // Defense-in-depth: drop any express-comment filing that slipped past proceedingsFilingRetrieval.
      const filteredFilings = (retrieval.filings as Array<Filing & { express_comment?: unknown }>).filter((f) => {
        const raw = f.express_comment;
        if (typeof raw === 'boolean') return !raw;
        if (typeof raw === 'number') return raw <= 0;
        if (typeof raw === 'string') return !(raw === '1' || raw.toLowerCase() === 'true');
        return true;
      }) as Filing[];

      // Filter out spam filers using batch evaluation (server-assisted, with client fallback)
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;

      const filerNames = filteredFilings.map(filing => filing.filers?.[0]?.name || 'Unknown_Filer');
      const { spamNames, reasonByName } = await batchCheckSpamFilers(filerNames, userId);

      let spamCount = 0;
      const filteredDebug: SpamFilteredFilingDebug[] = [];
      const spamMeta = new Map<string, { filerName: string; reason?: string }>();
      for (const filing of filteredFilings) {
        const filerName = filing.filers?.[0]?.name || 'Unknown_Filer';
        if (spamNames.has(filerName)) {
          spamCount++;
          spamMeta.set(filing.id_submission, {
            filerName,
            reason: reasonByName.get(filerName),
          });
          filteredDebug.push({
            submissionId: filing.id_submission,
            filerName,
            reason: reasonByName.get(filerName),
          });
        }
      }

      setSpamFilteredCount(spamCount);
      setSpamFilteredDebug(filteredDebug);
      setSpamBySubmissionId(spamMeta);
      setAllFilings(filteredFilings);
    } catch (err) {
      console.error('Error fetching filings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch filings');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [docketNumber, documentLimit]);

  // Raise the retrieval cap and refetch. The list is capped at documentLimit
  // (most-recent-first); on a mega docket that hides older filings, so we offer
  // an explicit "load more" instead of silently truncating.
  const handleLoadMoreFilings = useCallback(() => {
    setLoadingMore(true);
    setDocumentLimit((prev) => prev + 500);
  }, []);

  useEffect(() => {
    if (docketNumber) {
      fetchTimelineData();
    }
  }, [docketNumber]);

  useEffect(() => {
    if (docketNumber && timelineResolvedForDocket === docketNumber) {
      // Fetch filings once timeline lookup has resolved (DB timeline or parent synthetic PN).
      fetchFilings(activeTimeline);
    }
  }, [docketNumber, timelineResolvedForDocket, activeTimeline, fetchFilings]);

  const toggleExpansion = (submissionId: string) => {
    setExpandedFiling(expandedFiling === submissionId ? null : submissionId);
  };

  const getFilerName = (filing: Filing) => {
    if (filing.filers && filing.filers.length > 0) {
      return filing.filers[0].name;
    }
    return 'Unknown Filer';
  };

  const getFilingType = (filing: Filing) => {
    return filing.submissiontype?.short || filing.filing_type || 'UNKNOWN';
  };

  const visibleFilings = useMemo(() => {
    const base = hideSpamFilings
      ? allFilings.filter((filing) => !spamBySubmissionId.has(filing.id_submission))
      : allFilings;
    // Ex-parte view bypasses the comment/reply filter — ex partes are their own
    // filing type (stored under 'NOTICE'), not comments.
    if (exPartesOnly) {
      return base.filter((f) => (f as { is_ex_parte?: boolean }).is_ex_parte);
    }
    return base.filter(isCommentOrReplyFiling);
  }, [allFilings, hideSpamFilings, spamBySubmissionId, exPartesOnly]);

  const exParteCount = useMemo(
    () => allFilings.filter((f) => (f as { is_ex_parte?: boolean }).is_ex_parte).length,
    [allFilings],
  );

  // When the ex-parte view opens, fetch the per-filer advocacy positions
  // (who lobbied the Commission + what they urged). Computed + cached backend-side.
  useEffect(() => {
    if (!exPartesOnly || !docketNumber) return;
    let cancelled = false;
    setLoadingAdvocacy(true);
    setAdvocacyError(false);
    const base =
      (import.meta as any).env?.VITE_BACKEND_API_URL ||
      'https://fcc-summarize-app-r4ac3.ondigitalocean.app';
    fetch(`${base}/api/proceedings/${encodeURIComponent(docketNumber)}/ex-parte-advocacy`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!cancelled) setExParteAdvocacy(Array.isArray(d?.items) ? d.items : []);
      })
      .catch(() => {
        if (!cancelled) {
          setExParteAdvocacy([]);
          setAdvocacyError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAdvocacy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [exPartesOnly, docketNumber]);

  const filteredFilings = useMemo(() => {
    if (!searchTerm) return visibleFilings;
    const term = searchTerm.toLowerCase();
    return visibleFilings.filter(filing => {
      const filerName = getFilerName(filing).toLowerCase();
      const filingType = getFilingType(filing).toLowerCase();
      return filerName.includes(term) || filingType.includes(term);
    });
  }, [visibleFilings, searchTerm]);

  /**
   * Repair pass: make the Filings tab 1:1 with Supabase storage for the
   * Comment / Reply Comment rows that are visible to the user.
   *
   * Pipeline (per docket, runs once per (docket + filings-length) pair):
   *   1. Bulk-check uploaded_documents for all visible submissions.
   *   2. For submissions without a DB row, try to backfill from storage
   *      (buildSubmissionIdIndex) and insert a uploaded_documents row.
   *   3. Anything still missing is passed to uploadFilingsParallelWithResults,
   *      which will either succeed (recordDocumentUpload happens inside),
   *      classify the row as `unavailable` with a reason, or leave it as
   *      `failed` (we treat `failed` silently - the row still renders).
   */
  useEffect(() => {
    if (!docketNumber) return;
    if (loading || loadingTimeline) return;
    if (visibleFilings.length === 0) return;

    const gateKey = `${docketNumber}:${visibleFilings.length}`;
    if (repairRanRef.current === gateKey) return;
    repairRanRef.current = gateKey;

    let cancelled = false;

    const run = async () => {
      try {
        setRepairProgress({ phase: 'scan' });

        const submissionIds = visibleFilings.map((f) => f.id_submission);
        const dbRows = await fetchUploadedDocumentsChunked(submissionIds, docketNumber);

        const nextRetrievability = new Map<string, Retrievability>();
        for (const submissionId of dbRows.keys()) {
          nextRetrievability.set(submissionId, { status: 'uploaded' });
        }

        // Submissions without a DB row -> check storage directly before
        // triggering a re-upload. buildSubmissionIdIndex is a single paginated
        // storage scan, so reuse it for all missing rows.
        const missingFromDb = submissionIds.filter((id) => !nextRetrievability.has(id));

        if (missingFromDb.length > 0) {
          let storageIndex: Map<string, { filename: string; filingType: string }> | null = null;
          try {
            storageIndex = await buildSubmissionIdIndex(docketNumber);
          } catch (err) {
            console.warn('[filings-repair] storage index build failed', err);
          }

          if (cancelled) return;

          const stillMissing: Filing[] = [];
          for (const filing of visibleFilings) {
            const id = filing.id_submission;
            if (nextRetrievability.has(id)) continue;
            const hit = storageIndex?.get(id);
            if (hit) {
              nextRetrievability.set(id, { status: 'uploaded' });
              // Backfill uploaded_documents so future loads skip the scan.
              // Fire-and-forget; a failure here just means we re-scan next load.
              void recordDocumentUpload({
                submission_id: id,
                docket_number: docketNumber,
                file_name: hit.filename,
                filing_type: hit.filingType,
                storage_path: `ecfs/${docketNumber}/${hit.filingType}/${hit.filename}`,
                file_size: null,
                is_express_comment: false,
                source_url: null,
              }).catch((err) => {
                console.warn('[filings-repair] backfill recordDocumentUpload failed', err);
              });
            } else {
              stillMissing.push(filing);
            }
          }

          setRetrievability(new Map(nextRetrievability));

          if (!cancelled && stillMissing.length > 0) {
            // Cap how many missing filings we try to re-upload in a single
            // mount so a cold docket with thousands of fresh filings doesn't
            // hammer the upload-ecfs-files endpoint. Remaining rows stay
            // un-annotated and will be retried on the next mount.
            const batch = stillMissing.slice(0, MAX_REPAIR_UPLOADS_PER_RUN);
            setRepairProgress({ phase: 'upload', current: 0, total: batch.length });

            const minimal: MinimalFiling[] = batch.map((f) => ({
              id_submission: f.id_submission,
              filers: f.filers,
              submissiontype: f.submissiontype,
              filing_type: f.filing_type,
            }));

            const { outcomes } = await uploadFilingsParallelWithResults(
              minimal,
              docketNumber,
              (current, total) => {
                if (cancelled) return;
                setRepairProgress({ phase: 'upload', current, total });
              }
            );

            if (!cancelled) {
              for (const outcome of outcomes) {
                if (outcome.status === 'uploaded') {
                  nextRetrievability.set(outcome.submissionId, { status: 'uploaded' });
                } else if (outcome.status === 'unavailable') {
                  nextRetrievability.set(outcome.submissionId, {
                    status: 'unavailable',
                    reason: outcome.reason,
                  });
                }
                // `failed` / `skipped` -> leave unannotated so the UI doesn't
                // permanently block the row for what may be a transient issue.
              }
              setRetrievability(new Map(nextRetrievability));
            }
          }
        } else {
          setRetrievability(new Map(nextRetrievability));
        }
      } catch (err) {
        console.warn('[filings-repair] repair pass failed', err);
      } finally {
        if (!cancelled) setRepairProgress(null);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [docketNumber, visibleFilings, loading, loadingTimeline]);

  const getFilingPeriod = useCallback(
    (filing: Filing): 'comments' | 'reply_comments' | 'other' =>
      getProceedingFilingPeriod(filing, { periodSubmissionsMap, activeTimeline }),
    [periodSubmissionsMap, activeTimeline]
  );

  const categorizeFilingsByPeriod = useCallback((filingsToCategorize: Filing[]) => {
    const comments: Filing[] = [];
    const replyComments: Filing[] = [];
    const other: Filing[] = [];

    filingsToCategorize.forEach(filing => {
      const period = getFilingPeriod(filing);
      if (period === 'comments') {
        comments.push(filing);
      } else if (period === 'reply_comments') {
        replyComments.push(filing);
      } else {
        other.push(filing);
      }
    });

    return { comments, replyComments, other };
  }, [getFilingPeriod]);

  // Categorize filings by period (tab content + counts are based on this)
  const categorizedFilings = useMemo(() => {
    return categorizeFilingsByPeriod(filteredFilings);
  }, [filteredFilings, categorizeFilingsByPeriod]);

  const categorizedAllFilings = useMemo(() => {
    return categorizeFilingsByPeriod(visibleFilings);
  }, [visibleFilings, categorizeFilingsByPeriod]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const visibleReplyIds = categorizedFilings.replyComments.map(f => f.id_submission);
    const filteredReplyIds = spamFilteredDebug.map(item => item.submissionId);
    console.group(`[FilingsDebug] ${docketNumber} reply-period reconciliation`);
    console.log('Visible reply IDs in Filings tab:', visibleReplyIds);
    console.log('Spam-filtered submission IDs:', filteredReplyIds);
    console.log('Spam-filtered filing details:', spamFilteredDebug);
    console.log('Visible reply count:', visibleReplyIds.length, 'Spam filtered count:', spamFilteredDebug.length);
    console.groupEnd();
  }, [categorizedFilings.replyComments, spamFilteredDebug, docketNumber]);

  // Find file(s) for a filing submission ID
  const findFilesForFiling = useCallback(async (submissionId: string): Promise<{ filename: string; filingType?: string } | null> => {
    if (!docketNumber) return null;

    try {
      // uploaded_documents may store a display basename; storage objects use {submissionId}___{filer}___{name}
      const { data: documents, error: docError } = await supabase
        .from('uploaded_documents')
        .select('file_name, filing_type')
        .eq('submission_id', submissionId)
        .eq('docket_number', docketNumber)
        .limit(1);

      if (!docError && documents && documents.length > 0) {
        const row = documents[0];
        if (row.file_name && isStorageObjectKey(row.file_name, submissionId)) {
          return {
            filename: row.file_name,
            filingType: row.filing_type || undefined
          };
        }
      }

      // Fallback: search storage for files matching submission ID pattern
      // Files are stored as: {submissionId}___{filerName}___{fileName}
      const { data: subdirectories, error: listError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .list(`ecfs/${docketNumber}`, {
          limit: 1000,
          offset: 0,
        });

      if (listError) {
        console.warn('Failed to list docket subdirectories:', listError);
        return null;
      }

      const filingTypeFolders = (subdirectories || []).filter(item => item.id === null);

      for (const folder of filingTypeFolders) {
        const subdirectoryPath = `ecfs/${docketNumber}/${folder.name}`;
        
        const { data: typeFiles, error: typeListError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .list(subdirectoryPath, {
            limit: 1000,
            offset: 0,
          });

        if (!typeListError && typeFiles) {
          // Find file that starts with submission ID
          const matchingFile = typeFiles.find(file => {
            const parts = file.name.split('___');
            return parts.length >= 3 && parts[0] === submissionId;
          });

          if (matchingFile) {
            return {
              filename: matchingFile.name,
              filingType: folder.name
            };
          }
        }
      }

      return null;
    } catch (err) {
      console.error('Error finding files for filing:', err);
      return null;
    }
  }, [docketNumber]);

  const openDocumentForViewing = useCallback(
    async (submissionId: string) => {
      setOpeningDocumentIds((prev) => new Set(prev).add(submissionId));
      setError(null);
      try {
        const fileInfo = await findFilesForFiling(submissionId);
        if (!fileInfo?.filename) {
          setError('No processed document is available for this filing yet.');
          return;
        }
        const filingType = fileInfo.filingType || 'UNKNOWN';
        const storagePath = `ecfs/${docketNumber}/${filingType}/${fileInfo.filename}`;
        const { data, error: signErr } = await supabase.storage
          .from(STORAGE_BUCKET)
          .createSignedUrl(storagePath, 3600);
        if (signErr || !data?.signedUrl) {
          setError(signErr?.message || 'Could not open document.');
          return;
        }
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to open document.');
      } finally {
        setOpeningDocumentIds((prev) => {
          const next = new Set(prev);
          next.delete(submissionId);
          return next;
        });
      }
    },
    [docketNumber, findFilesForFiling]
  );

  // Generate individual summary for a filing (used by batch generation)
  const generateSummaryForFiling = useCallback(async (filing: Filing): Promise<{ success: boolean; error?: string }> => {
    const submissionId = filing.id_submission;
    
    // Check if filing is in comments or reply_comments period
    const period = getFilingPeriod(filing);
    if (period !== 'comments' && period !== 'reply_comments') {
      return { success: false, error: 'Summary generation is only available for filings in Comment Period or Reply Comment Period' };
    }

    // Defense in depth: rows the repair pass marked unavailable never have a
    // retrievable document, so short-circuit before we try to list storage.
    const retrievabilityEntry = retrievability.get(submissionId);
    if (retrievabilityEntry?.status === 'unavailable') {
      const reason = humanizeRetrievabilityReason(retrievabilityEntry.reason);
      setSummaryErrors((prev) => {
        const newMap = new Map(prev);
        newMap.set(submissionId, reason);
        return newMap;
      });
      return { success: false, error: reason };
    }

    try {
      setGeneratingSummaries(prev => new Set(prev).add(submissionId));
      setSummaryErrors(prev => {
        const newMap = new Map(prev);
        newMap.delete(submissionId);
        return newMap;
      });

      // Find file for this filing
      const fileInfo = await findFilesForFiling(submissionId);
      
      if (!fileInfo) {
        throw new Error('No file found for this filing. Please ensure the filing has been processed and uploaded.');
      }

      // Get filing type from filing or file info
      const filingType = (
        filing.filing_type ||
        filing.submissiontype?.short ||
        fileInfo.filingType ||
        'COMMENT'
      ).trim();

      // Generate summary
      const result = await generateIndividualSummary({
        docketNumber,
        filename: fileInfo.filename,
        filingType,
        submissionId,
        forceRefresh: false,
      });

      if (result.success && result.summary) {
        setGeneratedSummaries(prev => {
          const newMap = new Map(prev);
          newMap.set(submissionId, result.summary!);
          return newMap;
        });
        return { success: true };
      } else {
        throw new Error(result.error || 'Failed to generate summary');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate summary';
      setSummaryErrors(prev => {
        const newMap = new Map(prev);
        newMap.set(submissionId, errorMessage);
        return newMap;
      });
      return { success: false, error: errorMessage };
    } finally {
      setGeneratingSummaries(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  }, [docketNumber, findFilesForFiling, getFilingPeriod, retrievability]);

  // Generate summaries for all filings in the current period
  const handleGenerateAllSummaries = useCallback(async () => {
    if (isGeneratingAll) return;

    // Get filings for the current active period (only comments or reply_comments)
    const rawPeriodFilings = activePeriodTab === 'comments' 
      ? categorizedFilings.comments 
      : activePeriodTab === 'reply_comments'
      ? categorizedFilings.replyComments
      : [];

    // Exclude rows the repair pass marked as permanently unavailable so the
    // batch progress count reflects what will actually be summarized.
    const periodFilings = rawPeriodFilings.filter(
      (f) => retrievability.get(f.id_submission)?.status !== 'unavailable'
    );

    if (periodFilings.length === 0) {
      return;
    }

    try {
      setIsGeneratingAll(true);
      setBatchProgress({ current: 0, total: periodFilings.length, currentFiling: null });

      await runWithConcurrency(
        periodFilings,
        INDIVIDUAL_SUMMARY_BATCH_CONCURRENCY,
        async (filing) => {
          await generateSummaryForFiling(filing);
        },
        (completed, total) => {
          setBatchProgress({
            current: completed,
            total,
            currentFiling: null,
          });
        }
      );

      setBatchProgress({ current: periodFilings.length, total: periodFilings.length, currentFiling: null });
    } catch (err) {
      console.error('Error in batch summary generation:', err);
    } finally {
      setIsGeneratingAll(false);
      // Clear progress after a short delay
      setTimeout(() => {
        setBatchProgress(null);
      }, 2000);
    }
  }, [isGeneratingAll, activePeriodTab, categorizedFilings, generateSummaryForFiling, retrievability]);

  // Download all generated individual summaries as a single zip.
  // Mirrors handleDownloadAllCommentFilings but pulls from the summary
  // docx files written by prepare_individual_summary_page_aware (path:
  // summaries/{docket}/individual_summaries/{COMMENT-PERIOD|REPLY-COMMENT-PERIOD}/{file}.docx).
  // Filings whose summary hasn't been generated yet are silently skipped —
  // the user can see what made it in via the on-screen progress count
  // and the final "n/m" log line.
  const handleDownloadAllSummariesZip = useCallback(async () => {
    if (downloadZipProgress !== null) return;

    const allCommentFilings = [
      ...categorizedFilings.comments,
      ...categorizedFilings.replyComments,
    ].filter((f) => retrievability.get(f.id_submission)?.status !== 'unavailable');

    if (allCommentFilings.length === 0) {
      setError('No comment or reply comment filings available for summary download.');
      return;
    }

    const submissionIds = [...new Set(allCommentFilings.map((f) => f.id_submission))];

    try {
      setError(null);
      setDownloadZipProgress({ phase: 'index', current: 0, total: 0 });

      // We need each filing's storage basename to construct the summary
      // path (storage path is {sid}___{filer}___{file}.docx). Re-use the
      // same lookup the PDF zip uses — buildSubmissionIdIndex returns
      // ecfs/.../FILING_TYPE/{basename}.
      const [index, dbMap] = await Promise.all([
        buildSubmissionIdIndex(docketNumber),
        fetchUploadedDocumentsChunked(submissionIds, docketNumber),
      ]);

      const resolved: Array<{
        submissionId: string;
        filerName: string;
        baseName: string;          // {sid}___{filer}___{file}
        periodFolder: string;      // 'COMMENT-PERIOD' | 'REPLY-COMMENT-PERIOD'
      }> = [];

      for (const filing of allCommentFilings) {
        const submissionId = filing.id_submission;
        const filerName = getFilerName(filing);
        const dbRow = dbMap.get(submissionId);
        const info = resolveFileForBulkZip(submissionId, dbRow, index);
        if (!info) continue;
        // info.filingType is the storage folder name used by the original
        // upload (e.g. COMMENT). The summary path uses the upload_*_by_type
        // convention which normalizes COMMENT → COMMENT-PERIOD.
        const ft = (info.filingType || '').toUpperCase().replace(/_/g, '-');
        let periodFolder = ft;
        if (ft === 'COMMENT') periodFolder = 'COMMENT-PERIOD';
        else if (ft === 'REPLY-COMM' || ft === 'REPLY-COMMENT' || ft === 'REPLY') {
          periodFolder = 'REPLY-COMMENT-PERIOD';
        }
        resolved.push({
          submissionId,
          filerName,
          baseName: info.filename,
          periodFolder,
        });
      }

      if (resolved.length === 0) {
        setError('No files found. Generate summaries first, then try again.');
        return;
      }

      setDownloadZipProgress({ phase: 'download', current: 0, total: resolved.length });

      const filesToDownload: Array<{
        submissionId: string;
        filerName: string;
        baseName: string;
        blob: Blob;
      }> = [];
      const missing: string[] = [];

      await runWithConcurrency(
        resolved,
        BULK_DOWNLOAD_CONCURRENCY,
        async (item) => {
          // Summary docx is stored as `{baseName}.docx` — the backend
          // uploader appends .docx to the full PDF basename, so the
          // resulting key ends `.pdf.docx`. Build the path verbatim.
          const storagePath =
            `summaries/${docketNumber}/individual_summaries/${item.periodFolder}/${item.baseName}.docx`;
          try {
            const { data: fileBlob, error: downloadError } = await supabase.storage
              .from(STORAGE_BUCKET)
              .download(storagePath);
            if (downloadError || !fileBlob) {
              missing.push(`${item.filerName} (${item.submissionId})`);
              return;
            }
            filesToDownload.push({
              submissionId: item.submissionId,
              filerName: item.filerName,
              baseName: item.baseName,
              blob: fileBlob,
            });
          } catch (err) {
            missing.push(`${item.filerName} (${item.submissionId})`);
            console.warn('Summary docx fetch failed:', item.submissionId, err);
          }
        },
        (completed, total) => {
          setDownloadZipProgress({ phase: 'download', current: completed, total });
        }
      );

      if (filesToDownload.length === 0) {
        setError(
          'No summaries found in storage. Generate per-filing summaries first, then retry.'
        );
        return;
      }

      setDownloadZipProgress({ phase: 'zip', current: 0, total: 0 });

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Fetch the topic-level executive overview docx and add it to the
      // top of the zip (00_ prefix sorts it first). This is the
      // attorney-facing "read this first" document — synthesizes the
      // overarching topics + per-topic filer positions from every
      // individual summary in the period. Best-effort: if the endpoint
      // 404s (no individual summaries yet) or errors, we still ship the
      // zip with just the per-filer docs.
      try {
        const overviewUrl =
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/build-docket-executive-summary` +
          `?docket_number=${encodeURIComponent(docketNumber)}&filing_type=COMMENT`;
        const overviewResp = await fetch(overviewUrl, {
          method: 'GET',
          headers: { Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        });
        if (overviewResp.ok) {
          const overviewBlob = await overviewResp.blob();
          zip.file('00_Executive_Overview.docx', overviewBlob, { compression: 'STORE' });
        } else {
          console.warn(
            'Executive overview fetch returned',
            overviewResp.status,
            '— shipping zip without it.'
          );
        }
      } catch (err) {
        console.warn('Executive overview fetch failed, shipping zip without it:', err);
      }

      for (const file of filesToDownload) {
        const cleanFiler = file.filerName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
        // Flat per-filer file: {filer}_{sid}.docx so the same filer can
        // have multiple summaries without clobbering each other.
        const zipName = `${cleanFiler}__${file.submissionId}.docx`;
        zip.file(zipName, file.blob, { compression: 'STORE' });
      }

      if (missing.length > 0) {
        const manifest = [
          `Summaries downloaded: ${filesToDownload.length}`,
          `Summaries missing:    ${missing.length}`,
          '',
          'These filings did not have a generated summary in storage at',
          'the time of download — open the filing in CommsGenie and click',
          '"Generate summary" to create it, then re-run this zip:',
          '',
          ...missing.map((m) => `  - ${m}`),
        ].join('\n');
        zip.file('MISSING.txt', manifest);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `docket-${docketNumber}-summaries.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(
        `Summary zip: ${filesToDownload.length} / ${resolved.length} included; ${missing.length} missing.`
      );
    } catch (err) {
      console.error('Error downloading summaries zip:', err);
      setError(err instanceof Error ? err.message : 'Failed to download summaries.');
    } finally {
      setDownloadZipProgress(null);
    }
  }, [downloadZipProgress, categorizedFilings, docketNumber, retrievability]);

  // Download all comment and reply comment filings as zip
  const handleDownloadAllCommentFilings = useCallback(async () => {
    if (downloadZipProgress !== null) return;

    // Exclude rows the repair pass marked unavailable so we don't chase storage
    // keys that will never exist for the ZIP.
    const allCommentFilings = [
      ...categorizedFilings.comments,
      ...categorizedFilings.replyComments,
    ].filter((f) => retrievability.get(f.id_submission)?.status !== 'unavailable');

    if (allCommentFilings.length === 0) {
      setError('No comment or reply comment filings available to download.');
      return;
    }

    const submissionIds = [...new Set(allCommentFilings.map((f) => f.id_submission))];

    try {
      setError(null);
      setDownloadZipProgress({ phase: 'index', current: 0, total: 0 });

      const [index, dbMap] = await Promise.all([
        buildSubmissionIdIndex(docketNumber),
        fetchUploadedDocumentsChunked(submissionIds, docketNumber),
      ]);

      const resolved: Array<{
        submissionId: string;
        filerName: string;
        filename: string;
        filingType: string;
      }> = [];

      for (const filing of allCommentFilings) {
        const submissionId = filing.id_submission;
        const filerName = getFilerName(filing);
        const dbRow = dbMap.get(submissionId);
        const info = resolveFileForBulkZip(submissionId, dbRow, index);
        if (!info) {
          console.warn(`No file found for filing ${submissionId} (${filerName})`);
          continue;
        }
        resolved.push({
          submissionId,
          filerName,
          filename: info.filename,
          filingType: info.filingType,
        });
      }

      if (resolved.length === 0) {
        setError('No files found to download. Please ensure filings have been processed.');
        return;
      }

      setDownloadZipProgress({ phase: 'download', current: 0, total: resolved.length });

      const filesToDownload: Array<{
        filename: string;
        filingType: string;
        submissionId: string;
        filerName: string;
        blob: Blob;
      }> = [];

      await runWithConcurrency(
        resolved,
        BULK_DOWNLOAD_CONCURRENCY,
        async (item) => {
          const storagePath = `ecfs/${docketNumber}/${item.filingType}/${item.filename}`;
          try {
            const { data: fileBlob, error: downloadError } = await supabase.storage
              .from(STORAGE_BUCKET)
              .download(storagePath);

            if (downloadError || !fileBlob) {
              console.warn(`Failed to download file for filing ${item.submissionId}:`, downloadError);
              return;
            }

            filesToDownload.push({
              filename: item.filename,
              filingType: item.filingType,
              submissionId: item.submissionId,
              filerName: item.filerName,
              blob: fileBlob,
            });
          } catch (err) {
            console.error(`Error downloading file for filing ${item.submissionId}:`, err);
          }
        },
        (completed, total) => {
          setDownloadZipProgress({ phase: 'download', current: completed, total });
        }
      );

      if (filesToDownload.length === 0) {
        setError('No files found to download. Please ensure filings have been processed.');
        return;
      }

      setDownloadZipProgress({ phase: 'zip', current: 0, total: 0 });

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const filesByFiler: {
        [key: string]: Array<{ filename: string; blob: Blob; submissionId: string }>;
      } = {};

      filesToDownload.forEach((file) => {
        const cleanFilerName = file.filerName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');

        if (!filesByFiler[cleanFilerName]) {
          filesByFiler[cleanFilerName] = [];
        }
        filesByFiler[cleanFilerName].push({
          filename: file.filename,
          blob: file.blob,
          submissionId: file.submissionId,
        });
      });

      Object.keys(filesByFiler).forEach((filerName) => {
        const filerFiles = filesByFiler[filerName];

        filerFiles.forEach((file) => {
          const cleanFilename = file.filename.replace(/[<>:"/\\|?*]/g, '_');
          zip.file(`${filerName}/${cleanFilename}`, file.blob, { compression: 'STORE' });
        });
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `docket-${docketNumber}-comment-filings.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`Successfully downloaded zip file with ${filesToDownload.length} files`);
    } catch (err) {
      console.error('Error downloading comment filings:', err);
      setError(err instanceof Error ? err.message : 'Failed to download comment filings. Please try again.');
    } finally {
      setDownloadZipProgress(null);
    }
  }, [downloadZipProgress, categorizedFilings, docketNumber, retrievability]);

  // Initialize active tab based on available filings (only if current tab has no filings).
  // "Other Filings" is intentionally excluded — the Filings tab surfaces only
  // Comment/Reply Comment submissions within their respective periods.
  useEffect(() => {
    const currentTabCount =
      activePeriodTab === 'comments' ? categorizedFilings.comments.length :
      activePeriodTab === 'reply_comments' ? categorizedFilings.replyComments.length :
      0;

    if (currentTabCount === 0) {
      if (categorizedFilings.comments.length > 0) {
        setActivePeriodTab('comments');
      } else if (categorizedFilings.replyComments.length > 0) {
        setActivePeriodTab('reply_comments');
      }
    }
  }, [categorizedFilings, activePeriodTab]);

  // Get available tabs (only those with filings)
  const availableTabs = useMemo(() => {
    const tabs = [
      { id: 'comments' as const, label: 'Comment Period', count: categorizedFilings.comments.length },
      { id: 'reply_comments' as const, label: 'Reply Comment Period', count: categorizedFilings.replyComments.length },
    ];
    return tabs.filter(tab => tab.count > 0);
  }, [categorizedFilings]);

  // Get filings for active tab
  const activeTabFilings = useMemo(() => {
    // Ex-parte view is flat (all ex partes across periods), not period-sliced.
    if (exPartesOnly) return visibleFilings;
    switch (activePeriodTab) {
      case 'comments':
        return categorizedFilings.comments;
      case 'reply_comments':
        return categorizedFilings.replyComments;
      case 'other':
        return categorizedFilings.other;
      default:
        return [];
    }
  }, [activePeriodTab, categorizedFilings, exPartesOnly, visibleFilings]);

  const renderFilingCard = (filing: Filing) => {
    const filingTypeInfo = getFilingTypeTag(getFilingType(filing));
    const submissionId = filing.id_submission;
    const spamMeta = spamBySubmissionId.get(submissionId);
    const isSpamFiling = !!spamMeta;
    const period = getFilingPeriod(filing);
    const isInCommentPeriod = period === 'comments' || period === 'reply_comments';
    const isGenerating = generatingSummaries.has(submissionId);
    const hasSummary = generatedSummaries.has(submissionId);
    const summaryError = summaryErrors.get(submissionId);
    const summary = generatedSummaries.get(submissionId);
    const isSummaryExpanded = expandedSummaries.has(submissionId);
    const retrievabilityEntry = retrievability.get(submissionId);
    const isNotRetrievable = retrievabilityEntry?.status === 'unavailable';
    const notRetrievableReason = isNotRetrievable
      ? humanizeRetrievabilityReason(retrievabilityEntry?.reason)
      : null;

    // Download raw summary from card toolbar (same payload as expanded "Download raw")
    const handleDownloadSummary = () => {
      if (summary) {
        const blob = new Blob([summary], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `summary_${submissionId}_raw_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    };

    const openingDoc = openingDocumentIds.has(submissionId);

    return (
      <div
        key={submissionId}
        className="rounded-lg border border-gray-200 p-3 transition-colors hover:border-gray-300 hover:shadow-sm sm:p-4"
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-stone-100 text-stone-500"
            aria-hidden
          >
            <FileText className="h-4 w-4" />
          </div>

          <button
            type="button"
            className="min-w-0 flex-1 cursor-pointer rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40"
            onClick={() => toggleExpansion(submissionId)}
          >
            <p className="truncate text-sm font-semibold text-gray-900">{getFilerName(filing)}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-medium ${filingTypeInfo.bgColor} ${filingTypeInfo.textColor} ${filingTypeInfo.borderColor}`}
              >
                {filingTypeInfo.label}
              </span>
              {!hideSpamFilings && isSpamFiling && (
                <span className="inline-flex items-center rounded border border-yellow-300 bg-yellow-50 px-2 py-0.5 text-[11px] text-yellow-800">
                  Spam-filtered
                </span>
              )}
              {isNotRetrievable && (
                <span
                  className="inline-flex items-center rounded border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                  title={notRetrievableReason ?? undefined}
                >
                  Not retrievable for summary
                </span>
              )}
              {(filing.date_received || filing.date_submission) && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <Calendar className="h-3 w-3 shrink-0" />
                  {formatDateForDisplay(filing.date_received || filing.date_submission || '')}
                </span>
              )}
              <span className="font-mono text-[11px] text-gray-400">{submissionId}</span>
            </div>
          </button>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void openDocumentForViewing(submissionId);
              }}
              disabled={openingDoc || isNotRetrievable}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              title={
                isNotRetrievable
                  ? (notRetrievableReason ??
                    'Document is not available in storage; it cannot be viewed.')
                  : 'Open document'
              }
            >
              {openingDoc ? (
                <Loader className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">View</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void generateSummaryForFiling(filing);
              }}
              disabled={isGenerating || !isInCommentPeriod || isNotRetrievable}
              className="inline-flex items-center gap-1 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-400"
              title={
                isNotRetrievable
                  ? (notRetrievableReason ??
                    'Document is not available in storage; summaries cannot include it.')
                  : !isInCommentPeriod
                  ? 'Summaries are only available for comment or reply comment period filings'
                  : 'Generate AI summary'
              }
            >
              {isGenerating ? (
                <Loader className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">Summarize</span>
            </button>
            {hasSummary && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadSummary();
                }}
                className="rounded-md p-1.5 text-green-600 transition-colors hover:bg-green-50 hover:text-green-700"
                title="Download summary"
              >
                <Download className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => toggleExpansion(submissionId)}
              className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-expanded={expandedFiling === submissionId}
              aria-label={expandedFiling === submissionId ? 'Collapse details' : 'Expand details'}
            >
              {expandedFiling === submissionId ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
        
        {expandedFiling === submissionId && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="space-y-4">
              {/* Filing Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    Received: {formatDateForDisplay(filing.date_received || filing.date_submission || '')}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">Filing Type: </span>
                  <span className="text-gray-900">{getFilingType(filing)}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">Submission ID: </span>
                  <span className="text-gray-900 font-mono text-xs">{submissionId}</span>
                </div>
                <div>
                  <a
                    href={`https://www.fcc.gov/ecfs/search/search-filings/filing/${submissionId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View this filing on the FCC's official record (ECFS)
                  </a>
                </div>
              </div>

              {/* Summary Error */}
              {summaryError && (
                <div className="p-3 bg-red-50 border border-red-300 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{summaryError}</p>
                  </div>
                </div>
              )}

              {/* Generated Summary */}
              {hasSummary && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">Generated Summary</h4>
                    <button
                      type="button"
                      onClick={() => {
                        if (isSummaryExpanded) {
                          setExpandedSummaries((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(submissionId);
                            return newSet;
                          });
                        } else {
                          setExpandedSummaries((prev) => new Set(prev).add(submissionId));
                        }
                      }}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      {isSummaryExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                  {isSummaryExpanded && summary && (
                    <IndividualSummaryDigestPanel
                      rawSummary={summary}
                      downloadStem={`summary_${submissionId}`}
                      compact
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg p-6 shadow-sm mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-black">Filings</h2>
          {authoritativeCount != null && allFilings.length < authoritativeCount && (
            <span className="ml-3 text-sm text-gray-500 whitespace-nowrap">
              Showing {allFilings.length.toLocaleString()} of {authoritativeCount.toLocaleString()}
            </span>
          )}
          {spamFilteredCount > 0 && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Shield className="w-4 h-4 text-green-600" />
              <span>
                {spamFilteredCount} spam {spamFilteredCount === 1 ? 'filing' : 'filings'} {hideSpamFilings ? 'hidden' : 'shown'}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 px-2 py-1 border border-gray-200 rounded-lg">
            <input
              type="checkbox"
              checked={hideSpamFilings}
              onChange={(e) => setHideSpamFilings(e.target.checked)}
              disabled={spamFilteredCount === 0}
              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
            />
            <span>
              Hide spam filings
              {spamFilteredCount === 0 ? ' (none detected)' : ''}
            </span>
          </label>
          <button
            type="button"
            onClick={() => setExPartesOnly((v) => !v)}
            disabled={exParteCount === 0}
            aria-pressed={exPartesOnly}
            title="Ex parte filings reveal who is privately lobbying the Commission"
            className={`flex items-center gap-2 text-sm px-2 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              exPartesOnly
                ? 'bg-green-600 text-white border-green-600'
                : 'text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
          >
            <Shield className="w-4 h-4" />
            Ex partes{exParteCount > 0 ? ` (${exParteCount})` : ''}
          </button>
          {/* Generate All Summaries Button - only for Comment Period and Reply Comment Period */}
          {!loading && !loadingTimeline && (activePeriodTab === 'comments' || activePeriodTab === 'reply_comments') && activeTabFilings.length > 0 && (
            <button
              onClick={handleGenerateAllSummaries}
              disabled={isGeneratingAll}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm"
            >
              {isGeneratingAll ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Summarize Individual Documents</span>
                </>
              )}
            </button>
          )}
          {batchProgress && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Loader className="w-4 h-4 animate-spin text-green-600" />
              <span>
                {batchProgress.current} of {batchProgress.total}
              </span>
            </div>
          )}
          {downloadZipProgress && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Loader className="w-4 h-4 animate-spin text-blue-600" />
              <span>
                {downloadZipProgress.phase === 'index' && 'Scanning storage…'}
                {downloadZipProgress.phase === 'download' &&
                  `${downloadZipProgress.current} / ${downloadZipProgress.total} files`}
                {downloadZipProgress.phase === 'zip' && 'Building ZIP…'}
              </span>
            </div>
          )}
          {repairProgress && (
            <div
              className="flex items-center space-x-2 text-sm text-gray-600"
              title="Verifying that every visible filing has a retrievable document in storage"
            >
              <Loader className="w-4 h-4 animate-spin text-amber-600" />
              <span>
                {repairProgress.phase === 'scan' && 'Checking document availability…'}
                {repairProgress.phase === 'upload' &&
                  `Repairing ${repairProgress.current} / ${repairProgress.total}`}
              </span>
            </div>
          )}
          {/* Download All Comment Filings Button */}
          {!loading && !loadingTimeline && (categorizedFilings.comments.length > 0 || categorizedFilings.replyComments.length > 0) && (
            <button
              onClick={handleDownloadAllCommentFilings}
              disabled={downloadZipProgress !== null}
              className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              title="Download all Comment and Reply Comment filings (PDFs) as ZIP"
            >
              {downloadZipProgress !== null ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
            </button>
          )}
          {/* Download All Summaries Button — pulls the per-filing summary
              docx files generated by the page-aware backend pipeline. */}
          {!loading && !loadingTimeline && (categorizedFilings.comments.length > 0 || categorizedFilings.replyComments.length > 0) && (
            <button
              onClick={handleDownloadAllSummariesZip}
              disabled={downloadZipProgress !== null}
              className="p-2 text-green-700 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              title="Download all generated summaries (DOCX) as ZIP"
            >
              {downloadZipProgress !== null ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <FileText className="w-5 h-5" />
              )}
            </button>
          )}
          <button
            onClick={async () => {
              // Force-resync flow: explicitly tell the backend to fetch new
              // ECFS filings for this docket, wait for the ingest worker to
              // finish, then re-query the DB. Previously this button only
              // re-queried the DB without actually checking ECFS, which
              // misled users (icon + tooltip imply a real refresh).
              if (resyncing) return;
              if (!docketNumber) return;
              const activeTimeline = nprmTimelines[0] || null;

              setResyncing(true);
              setResyncStatus('Checking ECFS for new filings…');

              try {
                // 1. Snapshot the current sync state so we know when the
                //    worker has actually advanced it.
                const { data: beforeRow } = await supabase
                  .from('dockets')
                  .select('text_last_synced_at')
                  .eq('docket_number', docketNumber)
                  .maybeSingle();
                const beforeSyncedAt = beforeRow?.text_last_synced_at ?? null;

                // 2. POST to resync-docket. Returns immediately after
                //    enqueuing the text_sync job.
                const resyncUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resync-docket`;
                const res = await fetch(resyncUrl, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ docketNumber }),
                });
                if (!res.ok) {
                  const text = await res.text().catch(() => '');
                  throw new Error(`resync-docket ${res.status}: ${text}`);
                }
                const resyncBody = await res.json().catch(() => ({}));
                if (resyncBody?.alreadyRunning) {
                  setResyncStatus('A sync is already running — waiting for it to finish…');
                }

                // 3. Poll dockets.text_last_synced_at until it advances past
                //    the snapshot, or we time out. ingest-worker-poll cron
                //    fires every minute and our resync-docket also fires a
                //    fire-and-forget kick, so 90s is a generous ceiling.
                const POLL_MS = 3000;
                const TIMEOUT_MS = 90_000;
                const start = Date.now();
                let advanced = false;
                while (Date.now() - start < TIMEOUT_MS) {
                  await new Promise((r) => setTimeout(r, POLL_MS));
                  const { data: row } = await supabase
                    .from('dockets')
                    .select('text_last_synced_at')
                    .eq('docket_number', docketNumber)
                    .maybeSingle();
                  const current = row?.text_last_synced_at ?? null;
                  if (current && current !== beforeSyncedAt) {
                    advanced = true;
                    break;
                  }
                }

                if (!advanced) {
                  // Worker didn't advance the field in time. The job may
                  // still finish in the background — re-query anyway so the
                  // user sees whatever is in the DB now, but warn them.
                  setResyncStatus('Sync taking longer than expected. Showing latest known filings.');
                  setTimeout(() => setResyncStatus(null), 5000);
                }

                // 4. Pull fresh filing rows from the DB and re-render. If
                //    new filings landed, this is where they show up.
                await fetchFilings(activeTimeline);
                if (advanced) {
                  setResyncStatus(null);
                }
              } catch (err) {
                console.error('Resync error:', err);
                setResyncStatus(
                  `Sync failed: ${err instanceof Error ? err.message : 'unknown error'}`
                );
                setTimeout(() => setResyncStatus(null), 6000);
              } finally {
                setResyncing(false);
              }
            }}
            disabled={loading || loadingTimeline || resyncing}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={resyncStatus || 'Check ECFS for new filings'}
          >
            <RefreshCw className={`w-5 h-5 ${loading || loadingTimeline || resyncing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Resync status caption — surfaced inline so the user sees what the
          Reload button is actually doing while the worker runs in the
          background. Cleared when the sync completes (or after a short
          delay on error/timeout). */}
      {resyncStatus && (
        <div className="mb-3 text-sm text-gray-600 italic">
          {resyncStatus}
        </div>
      )}

      {/* Search Parameters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="fld2-FilingsSectiontsx-0" className="block text-sm font-medium text-gray-700 mb-1">
              Search Filings
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input id="fld2-FilingsSectiontsx-0"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by filer name or type..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 backdrop-blur-xl bg-red-50 border border-red-300 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation (hidden in the flat ex-parte view) */}
      {!loading && !loadingTimeline && !exPartesOnly && filteredFilings.length > 0 && availableTabs.length > 0 && (
        <div className="mb-4">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {availableTabs.map((tab) => {
                const isActive = activePeriodTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActivePeriodTab(tab.id)}
                    className={`flex items-center space-x-2 border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-emerald-600 text-emerald-800'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>
                      {tab.label} ({tab.count})
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}


      {/* Filings List */}
      {loading || loadingTimeline ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-6 h-6 text-green-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading filings...</span>
        </div>
      ) : filteredFilings.length === 0 ? (
        <p className="text-gray-600 text-sm py-8 text-center">
          {searchTerm ? 'No filings match your search.' : 'No filings found. Try adjusting your search parameters.'}
        </p>
      ) : availableTabs.length === 0 ? (
        <p className="text-gray-600 text-sm py-8 text-center">
          No filings available to display.
        </p>
      ) : (
        <div className="max-h-[min(70vh,560px)] space-y-2 overflow-y-auto pr-1">
          {exPartesOnly ? (
            loadingAdvocacy && exParteAdvocacy.length === 0 ? (
              <p className="text-gray-500 text-sm py-8 text-center">
                Summarizing ex parte advocacy…
              </p>
            ) : advocacyError && exParteAdvocacy.length === 0 ? (
              <p className="text-gray-500 text-sm py-8 text-center">
                We couldn't load ex parte advocacy right now. Please refresh in a moment.
              </p>
            ) : exParteAdvocacy.length > 0 ? (
              exParteAdvocacy.map((xp) => (
                <div key={xp.submission_id} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-gray-900 text-sm">{xp.filer}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{xp.date}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-700 leading-relaxed">{xp.position}</p>
                  <a
                    href={`https://www.fcc.gov/ecfs/search/search-filings/filing/${xp.submission_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block text-xs font-medium text-green-700 hover:underline"
                  >
                    View on ECFS ›
                  </a>
                </div>
              ))
            ) : (
              activeTabFilings.map((filing) => renderFilingCard(filing))
            )
          ) : (
            activeTabFilings.map((filing) => {
              return renderFilingCard(filing);
            })
          )}
        </div>
      )}

      {/* Results Count */}
      {!loading && !loadingTimeline && visibleFilings.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          {exPartesOnly ? (
            <>Showing {exParteAdvocacy.length} ex parte filing{exParteAdvocacy.length === 1 ? '' : 's'}</>
          ) : (
            <>
              Showing {activeTabFilings.length} of {
                activePeriodTab === 'comments'
                  ? categorizedAllFilings.comments.length
                  : activePeriodTab === 'reply_comments'
                    ? categorizedAllFilings.replyComments.length
                    : 0
              } filings
              {spamFilteredCount > 0 && (
                <span className="ml-2 text-green-600">
                  ({spamFilteredCount} spam {spamFilteredCount === 1 ? 'filing' : 'filings'} {hideSpamFilings ? 'hidden' : 'shown'})
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Truncation disclosure + load-more. Only when the retrieval actually hit
          the cap (retrievedCount >= documentLimit) AND ECFS reports more than we
          pulled — otherwise the gap between the filtered list and the ECFS total
          is just express/spam rows, not truncation, and "load more" would be a
          no-op. */}
      {!loading && !loadingTimeline && !exPartesOnly &&
        retrievedCount >= documentLimit &&
        authoritativeCount != null && authoritativeCount > retrievedCount && (
        <div className="mt-4 flex flex-col items-center gap-2 border-t border-gray-100 pt-4">
          <p className="text-center text-xs text-gray-500">
            Showing the most recent {retrievedCount.toLocaleString()} of{' '}
            {authoritativeCount.toLocaleString()} filings on this docket. Counts and
            summaries above reflect this subset.
          </p>
          <button
            onClick={handleLoadMoreFilings}
            disabled={loadingMore}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more filings'}
          </button>
        </div>
      )}
    </div>
  );
};


